import { Input, Modal, Select, Table } from "antd";
import { ColumnsType } from "antd/es/table";
import { useEffect, useState } from "react";
import { useQueryClient } from "react-query";
import { AppButton } from "components/button/AppButton";
import { useGetUserInfo } from "hooks/useGetUserInfo";
import { openNotification } from "utils/notification";
import { getAuthorizationErrorMessage } from "../authorizationError";
import { useAssignAssigneeRole } from "../hooks/useAssignAssigneeRole";
import {
  QUERY_KEY_FOR_ASSIGNEES,
  useGetAssignees,
} from "../hooks/useGetAssignees";
import { QUERY_KEY_FOR_USERS } from "../hooks/useFetchUsers";
import { useGetRoles } from "../hooks/useGetRoles";
import {
  AssignAssigneeRoleRequest,
  AuthorizationAssignee,
  AuthorizationGuard,
  AuthorizationRole,
} from "../types";

interface AssignRoleProps {
  open: boolean;
  guardName: AuthorizationGuard;
  initialRole: AuthorizationRole | null;
  handleClose: () => void;
}

const columns: ColumnsType<AuthorizationAssignee> = [
  {
    title: "Name",
    dataIndex: "name",
  },
  {
    title: "Email",
    dataIndex: "email",
  },
  {
    title: "Current role",
    render: (_, assignee) =>
      assignee.roles?.map((role) => role.name).join(", ") || "Unassigned",
  },
];

export const AssignRole = ({
  open,
  guardName,
  initialRole,
  handleClose,
}: AssignRoleProps) => {
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [selectedUserId, setSelectedUserId] = useState<string>();
  const [selectedRoleId, setSelectedRoleId] = useState<number>();
  const { token } = useGetUserInfo();
  const queryClient = useQueryClient();
  const { mutate, isLoading: assigning } = useAssignAssigneeRole();
  const { data: roles, isLoading: rolesLoading } = useGetRoles({ guardName });
  const { data: assignees, isLoading: assigneesLoading } = useGetAssignees({
    guardName,
    search,
    page,
    perPage: 20,
  });

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => window.clearTimeout(timeout);
  }, [searchInput]);

  useEffect(() => {
    if (open) {
      setSearchInput("");
      setSearch("");
      setPage(1);
      setSelectedUserId(undefined);
      setSelectedRoleId(initialRole?.id);
    }
  }, [guardName, initialRole, open]);

  const handleAssign = () => {
    if (!selectedUserId || !selectedRoleId) {
      openNotification({
        title: "Selection required",
        state: "info",
        description: "Select an assignee and a role",
      });
      return;
    }

    const request: AssignAssigneeRoleRequest = {
      token,
      userId: selectedUserId,
      guard_name: guardName,
      role_id: selectedRoleId,
    };

    mutate(request, {
      onError: (error) => {
        openNotification({
          title: "Error",
          state: "error",
          description: getAuthorizationErrorMessage(
            error,
            "Could not assign the role"
          ),
          duration: 6,
        });
      },
      onSuccess: () => {
        openNotification({
          title: "Success",
          state: "success",
          description: "Role assigned successfully",
          duration: 4.5,
        });
        queryClient.invalidateQueries([QUERY_KEY_FOR_ASSIGNEES, guardName]);
        if (guardName === "admins") {
          queryClient.invalidateQueries([QUERY_KEY_FOR_USERS]);
        }
        handleClose();
      },
    });
  };

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      footer={false}
      title={`Assign ${guardName === "admins" ? "Admin" : "User"} Role`}
      width="60rem"
      destroyOnHidden
    >
      <div className="py-5 space-y-4">
        <Input.Search
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
          placeholder="Search by name or email"
          allowClear
        />

        <Table<AuthorizationAssignee>
          rowKey="id"
          size="small"
          columns={columns}
          dataSource={assignees?.data ?? []}
          loading={assigneesLoading}
          rowSelection={{
            type: "radio",
            selectedRowKeys: selectedUserId ? [selectedUserId] : [],
            onChange: (keys) => setSelectedUserId(String(keys[0])),
          }}
          pagination={{
            current: assignees?.meta.current_page ?? page,
            pageSize: assignees?.meta.per_page ?? 20,
            total: assignees?.meta.total ?? 0,
            showSizeChanger: false,
            onChange: setPage,
          }}
          scroll={{ x: 550 }}
        />

        <Select
          className="w-full"
          value={selectedRoleId}
          onChange={setSelectedRoleId}
          loading={rolesLoading}
          placeholder="Select role"
          optionFilterProp="label"
          showSearch
          options={roles?.data.map((role) => ({
            value: role.id,
            label: role.name,
          }))}
        />

        <AppButton
          label="Assign Role"
          isLoading={assigning}
          disabled={!selectedUserId || !selectedRoleId}
          handleClick={handleAssign}
        />
      </div>
    </Modal>
  );
};
